import React, { useCallback, useEffect, useState } from 'react';
import { styled as styled2 } from '@compiled/react';
import mapValues from 'lodash/mapValues';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Badge from '@atlaskit/badge';
import { Box, xcss } from '@atlaskit/primitives';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current/src/main.tsx';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	SCREEN,
	FireScreenAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type { RouteResourceResponse } from '@atlassian/react-resource-router';
import { MAX_ITEMS_PER_PAGE } from '../../common/constants/tab-content.tsx';
import type { PrefetchResult } from '../../common/types/item-provider.tsx';
import type { Services } from '../../common/types/services.tsx';
import type { TabConfig } from '../../common/types/tabs.tsx';
import ErrorState from '../../common/ui/error-state/index.tsx';
import { interactionMetrics } from '../../common/utils/performance-analytics/index.tsx';
import { LoadNewActivityButton } from './load-new-activity-button/index.tsx';

export type TabContainerProps = {
	services: Services;
	prefetchResult: RouteResourceResponse<PrefetchResult>;
	defaultSelected: TabConfig;
	tabConfigs: TabConfig[];
	onTabChange: (tabId: string) => Promise<void>;
};

const getItemCounts = (
	tabConfig: Array<TabConfig>,
	prefetchResult: RouteResourceResponse<PrefetchResult>,
) => mapValues(prefetchResult?.data?.tabs || {}, (tab) => tab?.data?.total);

export const TabContainerFF = ({
	defaultSelected,
	prefetchResult,
	tabConfigs,
	services,
	onTabChange,
}: TabContainerProps): React.JSX.Element => {
	const currentUserData = useCurrentUser();
	const [maxItemsToShow, setMaxItemsToShow] = useState(MAX_ITEMS_PER_PAGE);
	const [itemCounts, setItemCounts] = useState(() => getItemCounts(tabConfigs, prefetchResult));
	const [selectedTabId, setSelectedTabId] = useState(defaultSelected.id);
	const tabConfigsWithTotal = tabConfigs.map((config) => ({
		...config,
		total: itemCounts[config.provider],
	}));

	const initialSelectedConfigIndex = tabConfigsWithTotal.findIndex(
		(config) => config.id === selectedTabId,
	);
	const [selectedConfigIndex, setSelectedConfigIndex] = useState(initialSelectedConfigIndex);

	useEffect(() => {
		if (defaultSelected.id !== selectedTabId) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
			setSelectedTabId(defaultSelected.id);
			const _selectedConfigIndex = tabConfigsWithTotal.findIndex(
				(config) => config.id === defaultSelected.id,
			);
			setSelectedConfigIndex(_selectedConfigIndex);
		}
	}, [defaultSelected.id, selectedTabId, tabConfigsWithTotal]);

	const onTotalChange = useCallback((providerKeyOrId: string, total: number) => {
		setItemCounts((prevItemCounts) => ({
			...prevItemCounts,
			[providerKeyOrId]: total,
		}));
	}, []);

	const onMoreItemsRequested = useCallback(() => {
		setMaxItemsToShow((prevMaxItemsToShow) => prevMaxItemsToShow + MAX_ITEMS_PER_PAGE);
	}, []);

	const handleUpdate = useCallback(
		(index: number, analyticsEvent: UIAnalyticsEvent) => {
			const selected = tabConfigs[index];
			setSelectedTabId(selected.id);

			selected.onClickCallback?.();

			// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly assigned: Metric; readonly workedon: Metric; readonly viewed: Metric; readonly starred: Metric; }'.
			interactionMetrics[selected.id].start();

			onTabChange(selected.id);

			fireUIAnalytics(analyticsEvent, 'listItemsTab', {
				itemTab: selected.id,
			});
		},
		[tabConfigs, onTabChange],
	);

	useEffect(() => {
		setItemCounts(getItemCounts(tabConfigs, prefetchResult));
	}, [tabConfigs, prefetchResult]);

	if (typeof services === 'undefined') {
		throw new Error('Services not configured for Your work tab container.');
	}

	const renderTab = (config: TabConfig) => {
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string | number' can't be used to index type 'Services'.
		const Provider = services[config.provider];
		const { tabContent: Content } = config;

		const selectedConfig = tabConfigsWithTotal[selectedConfigIndex];

		const isSelected = typeof selectedConfig !== 'undefined' && config.id === selectedConfig.id;

		return (
			<ContextualAnalyticsData key={config.label} attributes={{ itemTab: config.id }}>
				<JSErrorBoundary
					id="tab"
					packageName="jiraHome"
					fallback={() => (isSelected ? <ErrorState /> : null)}
				>
					<Provider
						id={config.id}
						providerKey={config.provider}
						prefetchResult={prefetchResult}
						selected={isSelected}
						onTotalChange={onTotalChange}
						userProviderData={currentUserData}
					>
						{/* @ts-expect-error - TS7006 - Parameter 'providerResult' implicitly has an 'any' type. */}
						{(providerResult) => {
							if (!isSelected) {
								return null;
							}

							return (
								<TabContentContainer>
									{providerResult?.updateAvailable && providerResult?.update && (
										<LoadNewActivityButton
											configId={selectedConfig?.id}
											onUpdate={providerResult.update}
										/>
									)}

									<Content
										itemProviderResult={providerResult}
										maxItems={maxItemsToShow}
										onLoadMoreClick={
											maxItemsToShow === MAX_ITEMS_PER_PAGE ? onMoreItemsRequested : undefined
										}
									/>
								</TabContentContainer>
							);
						}}
					</Provider>
				</JSErrorBoundary>
			</ContextualAnalyticsData>
		);
	};

	return (
		<ContextualAnalyticsData
			sourceType={SCREEN}
			sourceName="jiraHomeTab"
			attributes={{
				itemTab: defaultSelected.id,
			}}
		>
			<FireScreenAnalytics />
			<TabWrapper>
				<div data-testid="global-pages.home.ui.tab-container.navigation">
					<Tabs id="your-work-page-tabs" onChange={handleUpdate} selected={selectedConfigIndex}>
						<TabList>
							{tabConfigsWithTotal.map((tab) => (
								<Tab
									testId={`global-pages.home.ui.tab-container.navigation.item.${tab.id}`}
									key={tab.id}
								>
									{tab.label}
									{tab.showCount &&
										(typeof tab.total !== 'number' ? (
											<BadgeSkeleton />
										) : (
											<BadgeStyled>
												<Badge max={99}>{tab.total}</Badge>
											</BadgeStyled>
										))}
								</Tab>
							))}
						</TabList>
						{tabConfigsWithTotal.map((tab) => (
							<TabPanel key={tab.id}>{renderTab(tab)}</TabPanel>
						))}
					</Tabs>
				</div>
			</TabWrapper>
		</ContextualAnalyticsData>
	);
};

const BadgeSkeleton = () => (
	<Box as="span" xcss={BadgeSkeletonStyles}>
		&nbsp;&nbsp;
	</Box>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TabWrapper = styled2.div({
	margin: 0,
	padding: 0,
});

const BadgeSkeletonStyles = xcss({
	backgroundColor: 'color.background.neutral.hovered',
	color: 'color.text',
	borderRadius: 'radius.full',
	display: 'inline-block',
	font: 'font.body.UNSAFE_small',
	fontWeight: token('font.weight.regular'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 1,
	minWidth: token('space.025'),
	paddingBlock: 'space.050',
	paddingInline: 'space.075',
	textAlign: 'center',
	marginLeft: 'space.075',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BadgeStyled = styled2.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	marginLeft: `${token('space.075')} !important `,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TabContentContainer = styled2.div({
	position: 'relative',
	width: '100%',
	paddingTop: 0,
	paddingRight: 0,
	paddingBottom: token('space.075'),
	paddingLeft: 0,
});
